import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Stage {
  key: string;
  label: string;
  count: number;
  hint: string;
}

interface Overview {
  timezone: string;
  since: string;
  createdToday: number;
  deliveredToday: number;
  failedToday: number;
  cancelledToday: number;
  completionRate: number | null;
  openTotal: number;
  pipeline: Stage[];
}

/**
 * 今日概览。
 *
 * ★ 和异常台的分工:异常台答「有什么要我现在处理」,
 *   这里答「今天整体是什么形状」。两个都做成告警列表就重复了。
 *
 * 不做趋势折线:单店一天几十到几百单,那条线上是噪声不是信号。
 * 每个数字都要能回答一个调度问题,答不上的就不放。
 */
export function Overview({ onGoExceptions }: { onGoExceptions: () => void }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<Overview>('/orders/overview'));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div className="p-4 lg:p-6">
        <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-6 text-slate-400">加载中…</div>;

  const maxCount = Math.max(1, ...data.pipeline.map((s) => s.count));

  return (
    <div className="p-4 max-w-4xl lg:p-6">
      <div className="mb-1 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">今日概览</h1>
        {/* 时区要写出来 —— 跨时区运营时「今天」是哪一天必须没有歧义 */}
        <span className="text-xs text-slate-400">
          {data.timezone} ·{' '}
          {new Date(data.since).toLocaleString('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}{' '}
          起
        </span>
      </div>

      {/* ★ 指标卡一律带「今日」前缀,和下方「当前在手」的阶段名区分开 ——
             同样叫「未送达」但含义不同:这里是今天失败了几次,
             下面是此刻有几单处在未送达状态(昨天失败的今天还挂着) */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Metric label="今日进单" value={data.createdToday} />
        <Metric label="今日送达" value={data.deliveredToday} tone="good" />
        <Metric
          label="今日未送达"
          value={data.failedToday}
          tone={data.failedToday > 0 ? 'bad' : undefined}
        />
        <Metric
          label="今日已取消"
          value={data.cancelledToday}
          tone={data.cancelledToday > 0 ? 'bad' : undefined}
        />
        <Metric
          label="送达率"
          // ★ 还没有任何结果时显示「—」而不是 0% ——
          //   早上刚开工就看到 0% 会让人以为出了大问题
          value={data.completionRate == null ? '—' : `${data.completionRate}%`}
          tone={
            data.completionRate == null
              ? undefined
              : data.completionRate < 80
                ? 'bad'
                : 'good'
          }
          note={data.completionRate == null ? '今天还没有完成的单' : '分母是今天有结果的单'}
        />
      </div>

      <section className="mt-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            当前在手 {data.openTotal} 单
          </h2>
          <span className="text-xs text-slate-400">含昨天未送完的</span>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y
                        divide-slate-100 dark:divide-slate-700/50 bg-white dark:bg-slate-800">
          {data.pipeline.map((s) => (
            <div key={s.key} className="px-4 py-3 flex items-center gap-4">
              <span className="w-16 shrink-0 text-sm text-slate-600 dark:text-slate-400">
                {s.label}
              </span>
              <span className="w-10 shrink-0 text-right tabular-nums font-medium
                               text-slate-900 dark:text-slate-100">
                {s.count}
              </span>
              {/* 条形图是为了看出「哪一段鼓起来了」—— 那正是卡点所在 */}
              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    s.key === 'FAILED' ? 'bg-red-500' : 'bg-brand-500'
                  }`}
                  style={{ width: `${(s.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="w-44 shrink-0 text-xs text-slate-400">
                {s.count > 0 ? s.hint : ''}
              </span>
            </div>
          ))}
        </div>

        {data.pipeline.find((s) => s.key === 'FAILED')!.count > 0 && (
          <button
            onClick={onGoExceptions}
            className="mt-3 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800
                       text-sm text-red-700 dark:text-red-400"
          >
            去异常处理台 →
          </button>
        )}
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: number | string;
  tone?: 'good' | 'bad';
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white
                    dark:bg-slate-800 px-4 py-3">
      <div className="text-sm text-slate-500">{label}</div>
      <div
        className={`mt-0.5 text-3xl font-bold tabular-nums ${
          tone === 'good'
            ? 'text-green-600 dark:text-green-400'
            : tone === 'bad'
              ? 'text-red-600 dark:text-red-400'
              : 'text-slate-900 dark:text-slate-100'
        }`}
      >
        {value}
      </div>
      {note && <div className="mt-0.5 text-xs text-slate-400">{note}</div>}
    </div>
  );
}
